import { RestClient } from "./restClient";

/** A company's monthly spend guard, and where each half came from. */
export interface BudgetSetting {
  /** Is this company metered at all? Off unless somebody turned it on. */
  enabled: boolean;
  /** "company" when this company chose, "platform" when inheriting, "default"
   *  when nothing is set anywhere. Computed by the SERVER — comparing values
   *  client-side would mislabel a company that deliberately chose the same
   *  value as the platform default. */
  enabled_source: "company" | "platform" | "default";
  /** Monthly cap in USD. `null` means NO CAP — distinct from 0, which means
   *  block everything and is a legitimate deliberate pause. */
  limit_usd: number | null;
  limit_source: "company" | "platform" | "default";
  /** Whether THIS caller may change the limit. Only platform admins may: a cap
   *  the capped party can raise is not a cap. The panel uses this to explain
   *  the field rather than rendering one that 403s on save. */
  limit_editable: boolean;
}

/** `null` CLEARS a field so the company inherits again; omitting it leaves the
 *  field untouched. Those are different requests. */
export interface BudgetGuardUpdate {
  enabled?: boolean | null;
}

export interface BudgetLimitUpdate {
  limit_usd?: number | null;
}

export interface BudgetDefault {
  enabled: string | null;
  limit_usd: string | null;
}

/** Session-scoped cache, module scope: the panel unmounts on every tab switch
 *  in Organization settings, so a plain useEffect fetch would refire per
 *  click. Same reason as llmSettingsApi's cache. */
let cached: BudgetSetting | null = null;
let cachedDefault: BudgetDefault | null = null;

export function clearBudgetSettingCache(): void {
  cached = null;
  cachedDefault = null;
}

/** Wraps /v1/settings/budget. Methods throw RestError on non-2xx — notably
 *  403 when a company admin attempts the LIMIT, which the panel renders rather
 *  than swallowing. */
export class BudgetSettingsApi {
  constructor(private client: RestClient) {}

  async load(): Promise<BudgetSetting> {
    if (cached) return cached;
    cached = await this.client.get<BudgetSetting>("/v1/settings/budget");
    return cached;
  }

  /** Turn this company's guard on or off. Company admin. */
  async saveGuard(patch: BudgetGuardUpdate): Promise<BudgetSetting> {
    const saved = await this.client.put<BudgetSetting>("/v1/settings/budget", patch);
    cached = saved;
    return saved;
  }

  /** Set this company's cap. PLATFORM ADMIN ONLY — a 403 here is expected for
   *  a company admin and should be shown, not hidden. */
  async saveLimit(patch: BudgetLimitUpdate): Promise<BudgetSetting> {
    const saved = await this.client.put<BudgetSetting>(
      "/v1/settings/budget/limit",
      patch,
    );
    cached = saved;
    return saved;
  }

  async loadDefault(): Promise<BudgetDefault> {
    if (cachedDefault) return cachedDefault;
    cachedDefault = await this.client.get<BudgetDefault>("/v1/settings/budget/default");
    return cachedDefault;
  }

  /** Set the deployment-wide default limit. Clears the company cache too:
   *  what an inheriting company resolves to has just changed. */
  async saveDefault(patch: BudgetLimitUpdate): Promise<BudgetDefault> {
    const saved = await this.client.put<BudgetDefault>(
      "/v1/settings/budget/default",
      patch,
    );
    cachedDefault = saved;
    cached = null;
    return saved;
  }
}
